import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { COMPANY_INFO, SERVICES_LIST } from "../data/company";

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Close menus on route change
  useEffect(() => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
  }, [location.pathname]);

  // Handle scroll shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isServicesActive = location.pathname.startsWith("/vi-tilbyder") || location.pathname === "/ydelser";

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? "bg-white/95 backdrop-blur-md shadow-md py-3 border-b border-gray-200" : "bg-white py-4 border-b border-gray-200"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          
          {/* Logo */}
          <Link to="/" className="flex items-center group gap-3">
            <img 
              src={COMPANY_INFO.logoUrl} 
              alt={COMPANY_INFO.name} 
              className="h-8 sm:h-9 w-auto object-contain transition-transform duration-200 group-hover:scale-105" 
            />
          </Link>

          {/* Desktop Navigation (Visible >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center space-x-8">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 py-1 ${
                  isActive ? "text-[#c06228] font-semibold border-b-2 border-[#c06228]" : "text-gray-700 hover:text-[#c06228]"
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/om-kokholm-byg"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 py-1 ${
                  isActive ? "text-[#c06228] font-semibold border-b-2 border-[#c06228]" : "text-gray-700 hover:text-[#c06228]"
                }`
              }
            >
              Om Kokholm Byg
            </NavLink>

            {/* Ydelser Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 py-1 ${
                  isServicesActive ? "text-[#c06228] font-semibold border-b-2 border-[#c06228]" : "text-gray-700 hover:text-[#c06228]"
                }`}
                aria-expanded={dropdownOpen}
              >
                <span>Ydelser</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded shadow-xl py-2 z-50 max-h-[80vh] overflow-y-auto">
                  <Link
                    to="/ydelser"
                    className="block px-4 py-2.5 text-sm font-semibold text-[#c06228] hover:bg-gray-50 transition-colors border-b border-gray-100"
                  >
                    Alle ydelser
                  </Link>
                  {SERVICES_LIST.map((service) => (
                    <NavLink
                      key={service.slug}
                      to={`/vi-tilbyder/${service.slug.replace('vi-tilbyder-', '')}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                          isActive ? "bg-gray-100 text-[#c06228] font-semibold" : "text-gray-700 hover:bg-gray-50 hover:text-[#c06228]"
                        }`
                      }
                    >
                      <i className={`${service.icon} w-4 text-center text-[#c06228]`} aria-hidden="true" />
                      <span>{service.title}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 py-1 ${
                  isActive ? "text-[#c06228] font-semibold border-b-2 border-[#c06228]" : "text-gray-700 hover:text-[#c06228]"
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 py-1 ${
                  isActive ? "text-[#c06228] font-semibold border-b-2 border-[#c06228]" : "text-gray-700 hover:text-[#c06228]"
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>

          {/* Right Header Controls (Phone & CTA Button) */}
          <div className="hidden min-[1200px]:flex items-center gap-6">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="nowrap-contact flex items-center gap-2 text-sm font-medium text-gray-800 hover:text-[#c06228] transition-colors"
              title="Ring til os"
            >
              <i className="fa-solid fa-phone text-[#c06228]" aria-hidden="true" />
              <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
            </a>

            <Link
              to="/kontakt"
              className="btn-cta text-sm font-semibold px-5 py-2.5 rounded shadow hover:shadow-md transition-all transform active:scale-95 whitespace-nowrap text-white"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>

          {/* Hamburger Menu Button (Visible < 1200px) */}
          <div className="flex items-center gap-3 min-[1200px]:hidden">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="nowrap-contact flex items-center justify-center p-2 rounded bg-gray-100 text-[#c06228] hover:bg-gray-200 transition-colors"
              aria-label="Ring til Kokholm Byg"
            >
              <i className="fa-solid fa-phone text-base" aria-hidden="true" />
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 text-gray-700 hover:text-[#c06228] bg-gray-100 hover:bg-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#c06228]"
              aria-label="Åbn menu"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"} text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-gray-200 px-4 pt-3 pb-6 space-y-3 shadow-xl animate-fadeIn max-h-[calc(100vh-80px)] overflow-y-auto">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `block px-3 py-2 rounded text-base font-medium ${
                isActive ? "bg-gray-100 text-[#c06228] font-semibold" : "text-gray-700 hover:bg-gray-50"
              }`
            }
          >
            Forside
          </NavLink>

          <NavLink
            to="/om-kokholm-byg"
            className={({ isActive }) =>
              `block px-3 py-2 rounded text-base font-medium ${
                isActive ? "bg-gray-100 text-[#c06228] font-semibold" : "text-gray-700 hover:bg-gray-50"
              }`
            }
          >
            Om Kokholm Byg
          </NavLink>

          {/* Mobile Services Accordion */}
          <div>
            <button
              type="button"
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded text-base font-medium ${
                isServicesActive ? "bg-gray-100 text-[#c06228]" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span>Ydelser</span>
              <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${mobileDropdownOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>

            {mobileDropdownOpen && (
              <div className="pl-4 pt-1 pb-2 space-y-1 bg-gray-50 rounded mt-1 border border-gray-100">
                <Link
                  to="/ydelser"
                  className="block px-3 py-2 text-sm font-semibold text-[#c06228] hover:bg-gray-100 rounded"
                >
                  Alle ydelser
                </Link>
                {SERVICES_LIST.map((service) => (
                  <NavLink
                    key={service.slug}
                    to={`/vi-tilbyder/${service.slug.replace('vi-tilbyder-', '')}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 text-sm rounded ${
                        isActive ? "text-[#c06228] font-semibold bg-gray-100" : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    <i className={`${service.icon} w-4 text-center text-[#c06228]`} aria-hidden="true" />
                    <span>{service.title}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          <NavLink
            to="/galleri"
            className={({ isActive }) =>
              `block px-3 py-2 rounded text-base font-medium ${
                isActive ? "bg-gray-100 text-[#c06228] font-semibold" : "text-gray-700 hover:bg-gray-50"
              }`
            }
          >
            Galleri
          </NavLink>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `block px-3 py-2 rounded text-base font-medium ${
                isActive ? "bg-gray-100 text-[#c06228] font-semibold" : "text-gray-700 hover:bg-gray-50"
              }`
            }
          >
            Kontakt
          </NavLink>

          <div className="pt-4 border-t border-gray-200 space-y-3">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="nowrap-contact flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded font-medium text-sm border border-gray-200"
            >
              <i className="fa-solid fa-phone text-[#c06228]" aria-hidden="true" />
              <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
            </a>

            <Link
              to="/kontakt"
              className="btn-cta block text-center text-sm font-semibold px-5 py-3 rounded shadow text-white"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
